import { useContext, useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { Link } from 'react-router-dom';
import * as slideDataWCS from '../constants/slidesScopeWithoutCommonSense';

import { ChronoContext } from '../contexts/ChronoContext';
import { SlidesContext } from '../contexts/SlidesContext';
import { StepContext } from '../contexts/StepContext';
import { ButtonNext } from '../contexts/ButtonNext';
import { LanguageContext } from '../contexts/LanguageContext';
import { TestReviewModeContext } from '../contexts/TestReviewModeContext';

import PageStart from './Slides/PageStart';
import Informations from './Slides/Informations';
import PageRadioButton from './Slides/PageRadioButton';
import PageCB from './Slides/PageCB';
import PageTraitsPersonnalite from './Slides/PageTraitsPersonnalite';
import PageSituationStrategie from './Slides/PageSituationStrategie';
import PageMultiSlider from './Slides/PageMulitSlider';

import './TestReviewContent.css';
import '../generics/EditPageCharts.css';

const slidesWCS = slideDataWCS.slides;

const buildInitialAnswers = (slides) => {
    const maxInputIndex = slides.reduce((max, s) => {
        return Number.isInteger(s.inputNumber) ? Math.max(max, s.inputNumber) : max;
    }, 0);

    const answers = Array.from({ length: maxInputIndex + 1 }, () => ({}));

    slides.forEach((slide) => {
        if (!Number.isInteger(slide.inputNumber)) {
            return;
        }

        const index = slide.inputNumber;
        if (!answers[index]) {
            answers[index] = {};
        }

        if (typeof slide.key === 'string' && !Object.prototype.hasOwnProperty.call(answers[index], slide.key)) {
            answers[index][slide.key] = typeof slide.value !== 'undefined' ? slide.value : '';
        }

        if (Array.isArray(slide.inputsCB)) {
            slide.inputsCB.forEach((cb) => {
                if (cb && cb.key && !Object.prototype.hasOwnProperty.call(answers[index], cb.key)) {
                    answers[index][cb.key] = typeof cb.value !== 'undefined' ? cb.value : false;
                }
            });
        }

        if (Array.isArray(slide.inputsMS)) {
            slide.inputsMS.forEach((ms) => {
                if (ms && ms.key && !Object.prototype.hasOwnProperty.call(answers[index], ms.key)) {
                    answers[index][ms.key] = typeof ms.value !== 'undefined' ? ms.value : '0';
                }
            });
        }
    });

    return answers;
};

const getSavedAnswers = () => {
    try {
        const raw = sessionStorage.getItem('testReview_answers');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
        return null;
    }
};

const mergeAnswers = (baseAnswers, savedAnswers) => {
    if (!savedAnswers) return baseAnswers;

    const maxLen = Math.max(baseAnswers.length, savedAnswers.length);
    return Array.from({ length: maxLen }, (_, index) => ({
        ...(baseAnswers[index] || {}),
        ...(savedAnswers[index] || {})
    }));
};

const getStoredStep = (maxStep) => {
    try {
        const raw = sessionStorage.getItem('testReview_step');
        if (!raw) return 0;
        const parsed = Number(raw);
        if (Number.isNaN(parsed)) return 0;
        return Math.min(Math.max(parsed, 0), maxStep);
    } catch (e) {
        return 0;
    }
};

const getAdminAuthorities = () => {
    try {
        const raw = sessionStorage.getItem('testReview_adminAuthorities') || sessionStorage.getItem('adminAuthorities');
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

const getReviewEditLanguages = () => {
    try {
        const raw = sessionStorage.getItem('testReview_editLanguages');
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.map((lang) => String(lang).toLowerCase().trim());
    } catch (e) {
        return [];
    }
};

const getReviewTranslationLanguages = () => {
    try {
        const raw = sessionStorage.getItem('testReview_translationLanguages');
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.map((lang) => String(lang).toLowerCase().trim());
    } catch (e) {
        return [];
    }
};

const hasEditModeAccessForLocale = (locale) => {
    const code = sessionStorage.getItem('code');
    const isSuperAdmin = String(code) === '1000';
    if (isSuperAdmin) {
        return true;
    }

    const normalizedLocale = String(locale || '').toLowerCase().trim();
    if (!normalizedLocale) {
        return false;
    }

    const explicitLanguages = getReviewEditLanguages();
    if (explicitLanguages.length > 0) {
        return explicitLanguages.includes(normalizedLocale);
    }

    const adminAuthorities = getAdminAuthorities();
    if (!adminAuthorities) {
        return false;
    }

    const editLanguages = Array.isArray(adminAuthorities?.edit?.languages)
        ? adminAuthorities.edit.languages.map((lang) => String(lang).toLowerCase().trim())
        : [];
    const uniqueEditLanguages = Array.isArray(adminAuthorities?.uniqueEdit?.languages)
        ? adminAuthorities.uniqueEdit.languages.map((lang) => String(lang).toLowerCase().trim())
        : [];

    const canEditLocale = Boolean(
        editLanguages.includes(normalizedLocale)
    );

    const canUniqueEditLocale = Boolean(
        uniqueEditLanguages.includes(normalizedLocale)
    );

    return canEditLocale || canUniqueEditLocale;
};

const hasTranslationModeAccessForLocale = (locale) => {
    const code = sessionStorage.getItem('code');
    const isSuperAdmin = String(code) === '1000';
    if (isSuperAdmin) {
        return true;
    }

    const normalizedLocale = String(locale || '').toLowerCase().trim();
    if (!normalizedLocale) {
        return false;
    }

    const explicitLanguages = getReviewTranslationLanguages();
    if (explicitLanguages.length > 0) {
        return explicitLanguages.includes(normalizedLocale);
    }

    const adminAuthorities = getAdminAuthorities();
    const pairs = Array.isArray(adminAuthorities?.translator?.pairs)
        ? adminAuthorities.translator.pairs
        : [];

    return pairs.some((pair) => {
        const fromLang = String(pair?.from || '').toLowerCase().trim();
        const toLang = String(pair?.to || '').toLowerCase().trim();
        return fromLang === normalizedLocale || toLang === normalizedLocale;
    });
};

const reviewLanguages = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Francais' },
    { value: 'de', label: 'Deutsch' },
    { value: 'it', label: 'Italiano' },
    { value: 'es', label: 'Espanol' },
    { value: 'ja', label: 'Japanese' },
    { value: 'zh', label: 'Simplified Chinese' },
    { value: 'zh-TW', label: 'Traditional Chinese' }
];

export default function TestReviewContent() {
    const languageCtx = useContext(LanguageContext);
    const locale = languageCtx?.locale || 'en';
    const selectLanguage = languageCtx?.selectLanguage;

    const baseAnswers = useMemo(() => buildInitialAnswers(slidesWCS), []);
    const [chrono, setChrono] = useState({ hours: 0, minutes: 0, seconds: 0 });
    // Use slideIndex as the true array index for navigation
    const [slideIndex, setSlideIndex] = useState(() => getStoredStep(slidesWCS.length - 1));
    const [editMode, setEditMode] = useState(false);
    const [translationMode, setTranslationMode] = useState(false);
    const [buttonNext, setButtonNext] = useState(false);
    const [slidesAnswers, setSlidesAnswers] = useState(() => mergeAnswers(baseAnswers, getSavedAnswers()));

    // --- BEGIN: Add missing helpers and state for language/translation logic ---
    // Dummy implementation: adjust as needed for your real logic
    function getAllowedLanguagesForViewing() {
        // Example: allow all reviewLanguages
        return reviewLanguages.map(lang => lang.value);
    }

    function getAllowedTranslationPairs() {
        // Example: allow all pairs between reviewLanguages
        let pairs = [];
        for (let from of reviewLanguages) {
            for (let to of reviewLanguages) {
                if (from.value !== to.value) {
                    pairs.push({ from: from.value, to: to.value });
                }
            }
        }
        return pairs;
    }

    function normalizeReviewLanguage(lang) {
        return String(lang || '').toLowerCase().trim();
    }

    const [sourceLanguage, setSourceLanguage] = useState('fr');
    const [sourceMessages, setSourceMessages] = useState(null);
    const [targetMessages, setTargetMessages] = useState(null);
    // --- END: Add missing helpers and state ---
    const availableViewingLanguages = useMemo(() => {
        const allowedViewingValues = getAllowedLanguagesForViewing();
        return reviewLanguages.filter((lang) => allowedViewingValues.includes(lang.value));
    }, []);
    const allowedTranslationPairs = useMemo(() => getAllowedTranslationPairs(), []);
    const availableSourceLanguages = useMemo(() => {
        const allowedFromValues = Array.from(new Set(allowedTranslationPairs.map((pair) => pair.from)));
        return reviewLanguages.filter((lang) => allowedFromValues.includes(lang.value));
    }, [allowedTranslationPairs]);
    const availableTargetLanguages = useMemo(() => {
        const normalizedSource = normalizeReviewLanguage(sourceLanguage);
        const allowedToValues = Array.from(new Set(
            allowedTranslationPairs
                .filter((pair) => pair.from === normalizedSource)
                .map((pair) => pair.to)
        ));
        return reviewLanguages.filter((lang) => allowedToValues.includes(lang.value));
    }, [allowedTranslationPairs, sourceLanguage]);
    const canUseEditMode = useMemo(() => hasEditModeAccessForLocale(locale), [locale]);
    const canUseTranslationMode = useMemo(() => hasTranslationModeAccessForLocale(locale), [locale]);
    // Use slideIndex for all slide access (0-based)


    // --- FIX: Prevent unwanted override of user language selection ---
    useEffect(() => {
        if (availableViewingLanguages.length === 0 || typeof selectLanguage !== 'function') {
            return;
        }
        const normalizedLocale = normalizeReviewLanguage(locale);
        const hasValidViewingLocale = availableViewingLanguages.some((lang) => lang.value === normalizedLocale);
        if (!hasValidViewingLocale && availableViewingLanguages.length > 0) {
            if (!locale) {
                console.log('[TestReviewContent] Auto-selecting first available viewing language:', availableViewingLanguages[0].value);
                selectLanguage(availableViewingLanguages[0].value);
            } else {
                console.log('[TestReviewContent] NOT overriding user-selected language:', locale);
            }
        }
    }, [availableViewingLanguages, selectLanguage, locale, slideIndex]);

    useEffect(() => {
        if (availableSourceLanguages.length === 0) {
            return;
        }

        const normalizedSource = normalizeReviewLanguage(sourceLanguage);
        const hasValidSource = availableSourceLanguages.some((lang) => lang.value === normalizedSource);
        if (!hasValidSource) {
            setSourceLanguage(availableSourceLanguages[0].value);
        }
    }, [availableSourceLanguages, sourceLanguage]);

    // Load messages for source and target locales when translationMode enabled
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const src = sourceLanguage || 'fr';
                const tgt = locale || 'en';

                const [srcRes, tgtRes] = await Promise.all([
                    fetch(`/lang/${src}.json?t=${Date.now()}`),
                    fetch(`/lang/${tgt}.json?t=${Date.now()}`)
                ]);

                const [srcJson, tgtJson] = await Promise.all([srcRes.json(), tgtRes.json()]);
                if (!mounted) return;
                setSourceMessages(srcJson);
                setTargetMessages(tgtJson);
            } catch (e) {
                console.error('[TestReviewContent] Failed to load language files for split view', e);
                if (mounted) {
                    setSourceMessages(null);
                    setTargetMessages(null);
                }
            }
        };

        if (translationMode) load();
        else {
            setSourceMessages(null);
            setTargetMessages(null);
        }

        return () => { mounted = false; };
    }, [translationMode, sourceLanguage, locale]);


    // --- FIX: Prevent unwanted override of user language selection for translation target ---
    useEffect(() => {
        if (availableTargetLanguages.length === 0 || typeof selectLanguage !== 'function') {
            return;
        }
        const normalizedLocale = normalizeReviewLanguage(locale);
        const hasValidTarget = availableTargetLanguages.some((lang) => lang.value === normalizedLocale);
        if (!hasValidTarget && availableTargetLanguages.length > 0) {
            if (!locale) {
                console.log('[TestReviewContent] Auto-selecting first available target language:', availableTargetLanguages[0].value);
                selectLanguage(availableTargetLanguages[0].value);
            } else {
                console.log('[TestReviewContent] NOT overriding user-selected language (target):', locale);
            }
        }
    }, [availableTargetLanguages, locale, selectLanguage]);

    useEffect(() => {
        if (!canUseEditMode && editMode) {
            setEditMode(false);
        }
    }, [canUseEditMode, editMode]);

    useEffect(() => {
        if (!canUseTranslationMode && translationMode) {
            setTranslationMode(false);
        }
    }, [canUseTranslationMode, translationMode]);

    const updateValues = (index, key, value) => {
        setSlidesAnswers((prev) => {
            const next = [...prev];
            if (!next[index]) {
                next[index] = {};
            }

            next[index] = {
                ...next[index],
                [key]: value,
                [`${key}_timestamp`]: [{
                    hours: chrono.hours,
                    minutes: chrono.minutes,
                    seconds: chrono.seconds,
                    totalSeconds: (chrono.hours * 3600) + (chrono.minutes * 60) + chrono.seconds
                }]
            };

            sessionStorage.setItem('testReview_answers', JSON.stringify(next));
            return next;
        });

        setButtonNext(false);
    };

    // Set slide index directly (0-based)
    const setReviewStep = (nextIndex) => {
        const clamped = Math.min(Math.max(nextIndex, 0), slidesWCS.length - 1);
        setSlideIndex(clamped);
        sessionStorage.setItem('testReview_step', String(clamped));
    };

    const renderSlideType = (slide) => {
        if (!slide) return null;
        switch (slide.type) {
            case 'pageStart':
                return <PageStart slide={slide} />;
            case 'informations':
                return <Informations slide={slide} />;
            case 'pageRadioButton':
                return <PageRadioButton slide={slide} />;
            case 'pageSituationStrategy':
                return <PageSituationStrategie slide={slide} />;
            case 'pageCheckBox':
                return <PageCB slide={slide} />;
            case 'pageMultiSlider':
                return <PageMultiSlider slide={slide} />;
            case 'pageTraitsPersonnalite':
                return <PageTraitsPersonnalite slide={slide} />;
            case 'pageRadioFO':
                const PageRadioFO = require('./Slides/PageRadioFO').default;
                return <PageRadioFO slide={slide} />;
            case 'results':
                return (
                    <div className="TestReview-placeholder">
                        Final results slide is disabled in review mode.
                    </div>
                );
            default:
                return (
                    <div className="TestReview-placeholder">
                        Unsupported slide type: {String(slide.type)}
                    </div>
                );
        }
    };

    return (
        <SlidesContext.Provider value={{ slidesAnswers, updateValues }}>
            <ChronoContext.Provider value={{ chrono, setChrono }}>
                <StepContext.Provider value={{ step: slideIndex, setStep: setReviewStep }}>
                    <ButtonNext.Provider value={{ buttonNext, setButtonNext }}>
                        <div className="TestReview">
                            <div className="TestReview-header">
                                <div className="TestReview-titleWrap">
                                    <h2>SCOPE Questionnaire Review</h2>
                                    <span>Admin-only review mode with free navigation</span>
                                </div>
                                <div className="TestReview-controls">
                                    <select
                                        id="test-review-language"
                                        name="test-review-language"
                                        value={locale}
                                        onChange={(e) => {
                                            if (typeof selectLanguage === 'function') {
                                                selectLanguage(e.target.value);
                                            }
                                        }}
                                        aria-label="Select questionnaire language"
                                    >
                                        {reviewLanguages.map((lang) => (
                                            <option key={lang.value} value={lang.value}>
                                                {lang.label}
                                            </option>
                                        ))}
                                    </select>
                                    {canUseEditMode && (
                                        <button
                                            className={`TestReview-editModeButton ${editMode ? 'is-active' : ''}`}
                                            onClick={() => setEditMode((prev) => !prev)}
                                        >
                                            {editMode ? 'Edit Mode: On' : 'Edit Mode'}
                                        </button>
                                    )}
                                    {canUseTranslationMode && (
                                        <button
                                            className={`TestReview-translationModeButton ${translationMode ? 'is-active' : ''}`}
                                            onClick={() => setTranslationMode((prev) => !prev)}
                                        >
                                            {translationMode ? 'Translation Mode: On' : 'Translation Mode'}
                                        </button>
                                    )}
                                    <button onClick={() => setReviewStep(slideIndex - 1)} disabled={slideIndex <= 0}>Previous</button>
                                    <button onClick={() => setReviewStep(slideIndex + 1)} disabled={slideIndex >= slidesWCS.length - 1}>Next</button>
                                    <select
                                        id="test-review-slide"
                                        name="test-review-slide"
                                        value={slideIndex}
                                        onChange={(e) => setReviewStep(Number(e.target.value))}
                                    >
                                        {slidesWCS.map((slide, index) => (
                                            <option key={index} value={index}>
                                                {index + 1}. {slide.type}
                                            </option>
                                        ))}
                                    </select>
                                    <Link to="/manager" className="TestReview-backLink">Back to Manager</Link>
                                </div>
                            </div>

                            <div className="TestReview-meta">
                                Slide {slideIndex + 1} / {slidesWCS.length}
                            </div>

                            <TestReviewModeContext.Provider
                                value={{
                                    enabled: true,
                                    editMode,
                                    translationMode
                                }}
                            >
                                {translationMode ? (
                                    <div className="TestReview-translateSplit">
                                        <div className="TestReview-content--panel TestReview-content--sourcePanel">
                                            {sourceMessages ? (
                                                <IntlProvider messages={sourceMessages} locale={sourceLanguage}>
                                                    <div className="TestReview-content">
                                                        {slidesWCS[slideIndex] ? renderSlideType(slidesWCS[slideIndex]) : null}
                                                    </div>
                                                </IntlProvider>
                                            ) : (
                                                <div className="TestReview-content">Loading source language...</div>
                                            )}
                                        </div>

                                        <div className="TestReview-content--panel">
                                            {targetMessages ? (
                                                <IntlProvider messages={targetMessages} locale={locale}>
                                                    <div className="TestReview-content">
                                                        {slidesWCS[slideIndex] ? renderSlideType(slidesWCS[slideIndex]) : null}
                                                    </div>
                                                </IntlProvider>
                                            ) : (
                                                <div className="TestReview-content">Loading target language...</div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="TestReview-content">
                                        {slidesWCS[slideIndex] ? renderSlideType(slidesWCS[slideIndex]) : null}
                                    </div>
                                )}
                            </TestReviewModeContext.Provider>
                        </div>
                    </ButtonNext.Provider>
                </StepContext.Provider>
            </ChronoContext.Provider>
        </SlidesContext.Provider>
    );
}
